import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useWallet } from '../contexts/WalletContext';
import { useToast } from '../contexts/ToastContext';
import { apiFetch } from '../config/api';

interface CreateMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateMatchModal({ isOpen, onClose, onSuccess }: CreateMatchModalProps) {
  const { address: walletAddress } = useWallet();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  console.log('[CreateMatchModal] Rendered, isOpen:', isOpen, 'walletAddress:', walletAddress);

  const [formData, setFormData] = useState({
    title: '',
    sideA: '',
    sideB: '',
    matchTime: '',
    venue: '',
    sportType: 'pickleball'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[CreateMatchModal] Form submitted');
    setError('');
    setLoading(true);

    try {
      // 验证表单
      if (!formData.title || !formData.sideA || !formData.sideB || !formData.matchTime || !formData.venue) {
        console.error('[CreateMatchModal] Missing required fields');
        throw new Error('Please fill in all required fields');
      }

      if (!walletAddress) {
        console.error('[CreateMatchModal] Wallet not connected');
        throw new Error('Please connect your wallet');
      }

      console.log('[CreateMatchModal] Form data:', formData);
      console.log('[CreateMatchModal] Judge address:', walletAddress);

      // 验证时间
      const matchDate = new Date(formData.matchTime);
      if (matchDate <= new Date()) {
        console.error('[CreateMatchModal] Invalid match time');
        throw new Error('Match time must be in the future');
      }

      // 调用后端 API
      console.log('[CreateMatchModal] Calling API: /judge/match/create');
      const response = await apiFetch('/judge/match/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
          sideA: formData.sideA,
          sideB: formData.sideB,
          matchTime: formData.matchTime,
          venue: formData.venue,
          sportType: formData.sportType,
          judgeAddress: walletAddress
        }),
      });

      console.log('[CreateMatchModal] API response status:', response.status);
      const data = await response.json();
      console.log('[CreateMatchModal] API response data:', data);

      if (!response.ok || !data.success) {
        console.error('[CreateMatchModal] API error:', data.message);
        throw new Error(data.message || 'Failed to create match');
      }

      // 成功
      console.log('[CreateMatchModal] Match created successfully:', data.data.matchId);
      toast.success(`Match created successfully!\nMatch ID: ${data.data.matchId}\nTitle: ${data.data.title}`);
      
      // 重置表单
      setFormData({
        title: '',
        sideA: '',
        sideB: '',
        matchTime: '',
        venue: '',
        sportType: 'pickleball'
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('[CreateMatchModal] Error:', err);
      setError(err.message || 'Failed to create match. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        overflow: 'auto',
        padding: '20px',
        boxSizing: 'border-box',
      }}
      onClick={onClose}
    >
      <div 
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '500px',
          margin: '0 auto',
          marginTop: '20px',
          marginBottom: '20px',
          padding: '0',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">➕ Create New Match</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Match Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Match Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Championship Finals - Round 1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500">5-200 characters</p>
          </div>

          {/* Side A Team Name */}
          <div>
            <label htmlFor="sideA" className="block text-sm font-medium text-gray-700 mb-1">
              Side A Team Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="sideA"
              name="sideA"
              value={formData.sideA}
              onChange={handleChange}
              placeholder="e.g., Team Rockets"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            />
          </div>

          {/* Side B Team Name */}
          <div>
            <label htmlFor="sideB" className="block text-sm font-medium text-gray-700 mb-1">
              Side B Team Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="sideB"
              name="sideB"
              value={formData.sideB}
              onChange={handleChange}
              placeholder="e.g., Team Thunderbolts"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            />
          </div>

          {/* Match Date & Time */}
          <div>
            <label htmlFor="matchTime" className="block text-sm font-medium text-gray-700 mb-1">
              Match Date & Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              id="matchTime"
              name="matchTime"
              value={formData.matchTime}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
              min={new Date().toISOString().slice(0, 16)}
            />
            <p className="mt-1 text-xs text-gray-500">Must be a future date/time</p>
          </div>

          {/* Venue */}
          <div>
            <label htmlFor="venue" className="block text-sm font-medium text-gray-700 mb-1">
              Venue <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="venue"
              name="venue"
              value={formData.venue}
              onChange={handleChange}
              placeholder="e.g., Central Sports Arena"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            />
          </div>

          {/* Sport Type */}
          <div>
            <label htmlFor="sportType" className="block text-sm font-medium text-gray-700 mb-1">
              Sport Type
            </label>
            <select
              id="sportType"
              name="sportType"
              value={formData.sportType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            >
              <option value="pickleball">Pickleball</option>
              <option value="tennis">Tennis</option>
              <option value="badminton">Badminton</option>
            </select>
          </div>

          {/* Judge Address Display */}
          {walletAddress ? (
            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
              <p className="text-xs text-gray-600 mb-1">🏛️ Judge Address (Your Wallet):</p>
              <p className="text-sm font-mono text-indigo-700 font-semibold">
                {walletAddress.slice(0, 10)}...{walletAddress.slice(-8)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                You will be the judge for this match
              </p>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">
                ⚠️ Please connect your wallet to create a match
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Match'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // 使用 Portal 渲染到 document.body，確保在最頂層
  return createPortal(modalContent, document.body);
}

